import { useEffect, useRef, useState } from "react";
import { api, BACKEND_URL, formatApiError, tokenStore } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Camera, Check, Clock, ImagePlus, Instagram, Trash2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Trip photo sharing for customers. Uploaded photos wait for admin approval
 * before they appear on the Office TV / are considered for Instagram.
 */
export default function CustomerPhotoShare({ lang, bookingId }) {
  const fileRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState("");
  const [allowSocial, setAllowSocial] = useState(true);
  const [selected, setSelected] = useState(null); // File
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const T = (no, en) => (lang === "en" ? en : no);

  const load = async () => {
    try {
      const { data } = await api.get("/customer/photos");
      setPhotos(data || []);
    } catch {
      setPhotos([]);
    }
  };
  useEffect(() => { load(); }, []);

  const pick = (files) => {
    if (!files?.length) return;
    const f = files[0];
    if (!f.type.startsWith("image/")) {
      toast.error(T("Kun bildefiler er tillatt.", "Only image files are allowed."));
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error(T("Bildet er over 15 MB.", "The image is over 15 MB."));
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelected(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelected(null);
    setPreviewUrl("");
    setCaption("");
    setAllowSocial(true);
  };

  const submit = async () => {
    if (!selected) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", selected);
      fd.append("caption", caption);
      fd.append("allow_social", allowSocial ? "true" : "false");
      if (bookingId) fd.append("booking_id", bookingId);
      await api.post("/customer/photos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        T(
          "Takk! Bildet er sendt til godkjenning.",
          "Thanks! Your photo is queued for review.",
        ),
      );
      reset();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const removePending = async (id) => {
    if (!window.confirm(T("Fjerne bildet?", "Remove this photo?"))) return;
    try {
      await api.delete(`/customer/photos/${id}`);
      toast.success(T("Bilde fjernet.", "Photo removed."));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const statusBadge = (p) => {
    if (p.status === "approved") {
      return (
        <span
          data-testid={`photo-status-${p.id}`}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-emerald-800 bg-emerald-100 rounded px-2 py-0.5"
        >
          <Check className="w-3 h-3" />
          {T("Live på kontoret", "Live on office TV")}
        </span>
      );
    }
    if (p.status === "rejected") {
      return (
        <span
          data-testid={`photo-status-${p.id}`}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-red-700 bg-red-100 rounded px-2 py-0.5"
        >
          <X className="w-3 h-3" />
          {T("Avvist", "Rejected")}
        </span>
      );
    }
    return (
      <span
        data-testid={`photo-status-${p.id}`}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-amber-800 bg-amber-100 rounded px-2 py-0.5"
      >
        <Clock className="w-3 h-3" />
        {T("Under gjennomgang", "Awaiting review")}
      </span>
    );
  };

  return (
    <Card className="p-6 sm:p-8" data-testid="customer-photo-share-card">
      <div className="flex items-center gap-2 mb-3">
        <Instagram className="w-5 h-5 text-[#A57F39]" />
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#A57F39]">
          {T("Del turbildene dine", "Share your trip photos")}
        </div>
      </div>
      <h2 className="text-xl font-semibold text-[#0C1824]">
        {T(
          "Vi vil elske å se turen din!",
          "We'd love to see your trip!",
        )}
      </h2>
      <p className="text-sm text-[#2B3F4D] mt-1">
        {T(
          "Send oss dine beste øyeblikk. Godkjente bilder vises på storskjermen på kontoret vårt, og kan bli med i vår Instagram-feed.",
          "Send us your best moments. Approved photos appear on our office screen and may be featured on our Instagram feed.",
        )}
      </p>

      {/* Upload panel */}
      <div className="mt-5 border border-dashed border-[#BD984D]/40 rounded-xl p-5 bg-[#BD984D]/5">
        {!selected ? (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            data-testid="customer-photo-pick"
            className="w-full flex flex-col items-center justify-center py-8 text-[#A57F39] hover:bg-[#BD984D]/10 rounded-lg transition"
          >
            <ImagePlus className="w-10 h-10" />
            <div className="mt-3 font-semibold text-[#0C1824]">
              {T("Velg et bilde å dele", "Pick a photo to share")}
            </div>
            <div className="text-xs text-[#2B3F4D] mt-1">
              {T("JPG, PNG, HEIC eller WEBP – opptil 15 MB.", "JPG, PNG, HEIC or WEBP – up to 15 MB.")}
            </div>
          </button>
        ) : (
          <div className="space-y-4">
            <div className="relative rounded-lg overflow-hidden bg-slate-100 max-h-96">
              <img
                src={previewUrl}
                alt=""
                className="w-full max-h-96 object-contain"
                data-testid="customer-photo-preview"
              />
              <button
                type="button"
                onClick={reset}
                data-testid="customer-photo-clear"
                className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5"
                aria-label={T("Fjern bilde", "Remove photo")}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div>
              <Textarea
                rows={2}
                value={caption}
                maxLength={280}
                onChange={(e) => setCaption(e.target.value)}
                placeholder={T(
                  "Legg til en kort tekst (valgfritt)",
                  "Add a short caption (optional)",
                )}
                data-testid="customer-photo-caption"
              />
              <div className="text-[10px] text-slate-500 mt-1 text-right">
                {caption.length}/280
              </div>
            </div>
            <label
              className="flex items-start gap-2 text-sm text-[#2B3F4D] cursor-pointer select-none"
              data-testid="customer-photo-social-label"
            >
              <input
                type="checkbox"
                checked={allowSocial}
                onChange={(e) => setAllowSocial(e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#BD984D]"
                data-testid="customer-photo-social-toggle"
              />
              <span>
                {T(
                  "Ja, kontoret kan bruke bildet mitt på Instagram / markedsføring.",
                  "Yes, the office may use my photo on Instagram / marketing.",
                )}
              </span>
            </label>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={submit}
                disabled={uploading}
                data-testid="customer-photo-submit"
                className="bg-[#BD984D] hover:bg-[#A57F39] text-white h-11 flex-1 sm:flex-none"
              >
                <Camera className="w-4 h-4 mr-2" />
                {uploading
                  ? T("Sender…", "Sending…")
                  : T("Send inn bildet", "Submit photo")}
              </Button>
              <Button
                type="button"
                onClick={reset}
                variant="outline"
                className="h-11"
                data-testid="customer-photo-cancel"
              >
                {T("Avbryt", "Cancel")}
              </Button>
            </div>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => pick(e.target.files)}
        />
      </div>

      {/* Existing photos */}
      {photos.length > 0 && (
        <div className="mt-6">
          <div className="text-xs uppercase tracking-[0.22em] text-[#A57F39] font-semibold mb-3">
            {T("Bildene dine", "Your photos")}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="customer-photo-list">
            {photos.map((p) => (
              <div
                key={p.id}
                data-testid={`customer-photo-item-${p.id}`}
                className="relative rounded-lg overflow-hidden border border-slate-200 bg-slate-50 aspect-square"
              >
                {p.status === "approved" ? (
                  <img
                    src={`${BACKEND_URL}/api/public/customer-photo/${p.id}`}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img
                    src={`${BACKEND_URL}/api/customer/photos/${p.id}/raw?auth=${encodeURIComponent(tokenStore.get())}`}
                    alt=""
                    className="w-full h-full object-cover blur-[1px] opacity-70"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                )}
                <div className="absolute top-2 left-2">{statusBadge(p)}</div>
                {p.status !== "approved" && (
                  <button
                    type="button"
                    onClick={() => removePending(p.id)}
                    data-testid={`customer-photo-delete-${p.id}`}
                    className="absolute top-2 right-2 bg-black/60 hover:bg-red-600 text-white rounded-full p-1.5"
                    title={T("Fjern", "Remove")}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                {p.status === "rejected" && p.review_notes && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[10px] p-1.5 line-clamp-2">
                    {p.review_notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
